import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { adminUseCase } from "../../../di/container";
import ConfirmMessageModal from "../../components/UI/ConfirmMessageModal";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";
import type { LandingUserInfoResponse } from "domain/types/shared";

// ───────────────────────────────────────────────────────────────────────────
// helpers (Users.tsx / ManageRegisteredCompanies1.tsx 와 동일 컨셉)
// ───────────────────────────────────────────────────────────────────────────

const FONT_FAMILY = "Pretendard, -apple-system, BlinkMacSystemFont, sans-serif";

const PAGE_SIZE = 50;

/** 사용자 식별 색상 풀 (Users.tsx 의 Avatar 와 동일 컨셉). */
const AVATAR_PALETTE = [
  "#4285F4",
  "#27AE60",
  "#5A4FCF",
  "#B26A00",
  "#C53030",
  "#1170BF",
  "#A12A6F",
  "#1F7A3F",
  "#1A4FA5",
];

const pickAvatarColor = (seed: number): string =>
  AVATAR_PALETTE[Math.abs(seed) % AVATAR_PALETTE.length];

/** 회사명 → 2글자 약어. 영문/숫자는 앞 2글자, 그 외(한글 등)는 첫 글자. */
const buildLogoCode = (name: string | null | undefined): string => {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "??";
  if (/^[A-Za-z0-9]/.test(trimmed)) {
    return trimmed.slice(0, 2).toUpperCase();
  }
  return trimmed.charAt(0);
};

/** "yyyy-MM-ddTHH:mm:ss[...]" → "yyyy. MM. DD." */
/** 1234567890 → 123-45-67890 */
const formatBizNo = (value: string): string =>
  /^\d{10}$/.test(value) ? `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}` : value;

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}. ${match[2]}. ${match[3]}.`;
  return value;
};

const Avatar: React.FC<{ char: string; color: string }> = ({ char, color }) => (
  <span
    className="rounded-full inline-flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
    style={{ width: 28, height: 28, background: color }}
  >
    {char}
  </span>
);

const LogoMini: React.FC<{ code: string }> = ({ code }) => (
  <span
    className="rounded-md inline-flex items-center justify-center text-[10px] font-bold flex-shrink-0"
    style={{ width: 30, height: 30, background: "#F0F1F4", color: "#555" }}
  >
    {code}
  </span>
);

const thClass =
  "bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-[18px] text-left border-b border-[#D9D9D9]";
const tdClass = "py-3 px-[18px] text-[13px] border-b border-[#D9D9D9]";

const inputClass =
  "w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white";
const resetBtnClass =
  "inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] min-w-[85px]";

const approveBtnClass =
  "inline-flex items-center justify-center font-medium rounded-lg px-3 py-1.5 text-[12px] bg-[#4285F4] text-white hover:bg-[#3367D6]";
const deleteBtnClass =
  "inline-flex items-center justify-center font-medium rounded-lg px-3 py-1.5 text-[12px] bg-white text-[#555] border border-[#D9D9D9] hover:bg-[#F9FAFB]";

// ───────────────────────────────────────────────────────────────────────────
// main page
// ───────────────────────────────────────────────────────────────────────────

export const CompanyRequests: React.FC = () => {
  const [requests, setRequests] = useState<LandingUserInfoResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  // 승인/삭제 후 목록을 처음부터 다시 불러오기 위한 트리거
  const [refreshKey, setRefreshKey] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchKeyword, setSearchKeyword] = useState("");

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasMore = requests.length < totalElements;

  // 페이지 로드 (page 0 = reset, page 1+ = append)
  useEffect(() => {
    let cancelled = false;
    const fetchPage = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminUseCase.getRequestedCompany({
          page,
          size: PAGE_SIZE,
          sort: ["createdAt", "DESC"],
        });
        if (cancelled) return;
        const items = res.items ?? [];
        setRequests((prev) => (page === 0 ? items : [...prev, ...items]));
        setTotalElements(res.totalElements ?? 0);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "업체 등록 요청 목록을 불러오지 못했습니다.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchPage();
    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  // 무한 스크롤 — sentinel 이 보이면 다음 페이지 자동 로드
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  const reload = (): void => {
    setPage(0);
    setRefreshKey((k) => k + 1);
  };

  const filteredRequests = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return requests;
    return requests.filter(
      (r) =>
        (r.requestedCompanyName ?? "").toLowerCase().includes(keyword) ||
        (r.name ?? "").toLowerCase().includes(keyword) ||
        (r.email ?? "").toLowerCase().includes(keyword),
    );
  }, [requests, searchKeyword]);

  // ───── 승인/삭제 확인 모달 + 알림 ─────
  const [message, setMessage] = useState("");
  const [submitFunction, setSubmitFunction] = useState<() => void>(() => {});

  const { notifications, addItem: addNotificationItem } = useNotifications();

  const confirmCancel = (row: LandingUserInfoResponse): void => {
    setMessage(`${row.requestedCompanyName} 업체 등록 요청을 삭제할까요?`);
    setSubmitFunction(() => () => {
      void (async () => {
        await adminUseCase.cancelRequestedCompany(row.id);
        addNotificationItem("삭제되었습니다.");
        reload();
        setMessage("");
        setSubmitFunction(() => {});
      })();
    });
  };

  const confirmApprove = (row: LandingUserInfoResponse): void => {
    setMessage(
      row.companyRequest
        ? `${row.requestedCompanyName}(${formatBizNo(row.companyRequest.businessNumber)}) 업체 등록 요청을 승인할까요?`
        : `${row.requestedCompanyName} 업체 등록 요청을 승인할까요?`,
    );
    setSubmitFunction(() => () => {
      void (async () => {
        await adminUseCase.approveCompany(row.id);
        addNotificationItem("승인되었습니다.");
        reload();
        setMessage("");
        setSubmitFunction(() => {});
      })();
    });
  };

  return (
    <div>
      {message ? (
        <ConfirmMessageModal
          message={message}
          setMessage={setMessage}
          submitFunction={submitFunction}
        />
      ) : null}
      <NotificationsList notifications={notifications} />

      <Pod24AdminLayout>
        <div className="text-[#252525]" style={{ fontFamily: FONT_FAMILY }}>
        {/* 페이지 헤더 */}
        <div className="mb-6">
          <h1 className="text-[22px] font-bold text-[#222]">신규 업체 등록</h1>
          <p className="text-[13px] text-[#888] mt-1">
            총 {totalElements}건의 업체 등록 요청이 대기 중입니다. 승인 시
            신청자가 해당 업체의 관리자(COMPANY_ADMIN)가 됩니다.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg border border-[#4285F4]/20 bg-[#EBF3FF] text-[13px] text-[#4285F4]">
            {error}
          </div>
        )}

        {/* 검색 박스 */}
        <div className="mb-5 p-5 border border-[#D9D9D9] rounded-[10px] bg-white flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className={`flex-1 ${inputClass}`}
            placeholder="🔍 업체명, 신청자명, 이메일로 검색"
          />
          <button
            onClick={() => setSearchKeyword("")}
            className={resetBtnClass}
          >
            초기화
          </button>
        </div>

        {/* 테이블 */}
        <div className="rounded-lg overflow-x-auto border border-[#D9D9D9] bg-white mb-4">
          <table
            className="w-full"
            style={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <thead>
              <tr>
                <th className={thClass}>업체명</th>
                <th className={`${thClass} w-[160px]`}>신청자명</th>
                <th className={`${thClass} w-[220px]`}>신청자 이메일</th>
                <th className={`${thClass} w-[140px]`}>연락처</th>
                <th className={`${thClass} w-[110px]`}>가입일</th>
                <th className={`${thClass} w-[180px] text-center`}>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && requests.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                  >
                    불러오는 중...
                  </td>
                </tr>
              )}
              {!loading && filteredRequests.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-10 px-[18px] text-[13px] text-center text-[#888]"
                  >
                    {searchKeyword.trim()
                      ? "검색 조건에 해당하는 등록 요청이 없습니다."
                      : "대기 중인 업체 등록 요청이 없습니다."}
                  </td>
                </tr>
              )}
              {filteredRequests.map((row) => (
                <tr key={row.id} className="hover:bg-[#F2F2F2]">
                  <td className={tdClass}>
                    <div className="flex items-center gap-3">
                      <LogoMini code={buildLogoCode(row.requestedCompanyName)} />
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium">
                          {row.requestedCompanyName ?? "—"}
                        </span>
                        {row.companyRequest ? (
                          <span className="text-[12px] text-[#757575] leading-[1.5]">
                            {formatBizNo(row.companyRequest.businessNumber)} · 대표 {row.companyRequest.ceoName}
                            <br />
                            {row.companyRequest.businessType} / {row.companyRequest.businessCategory} · {row.companyRequest.phone}
                            <br />
                            {[row.companyRequest.address, row.companyRequest.addressDetail].filter(Boolean).join(" ")}
                          </span>
                        ) : (
                          <span className="text-[12px] text-[#888]">
                            업체명만 받은 예전 신청 — 승인 후 내 회사 정보에서 사업자 정보 입력 필요
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={tdClass}>
                    <div className="flex items-center gap-2.5">
                      <Avatar
                        char={(row.name ?? "?").charAt(0) || "?"}
                        color={pickAvatarColor(row.id)}
                      />
                      <span>{row.name ?? "—"}</span>
                    </div>
                  </td>
                  <td className={tdClass}>
                    {row.email ? (
                      <a
                        href={`mailto:${row.email}`}
                        className="text-[#4285F4] hover:underline"
                      >
                        {row.email}
                      </a>
                    ) : (
                      <span className="text-[#888]">—</span>
                    )}
                  </td>
                  <td className={`${tdClass} text-[#555]`}>
                    {row.mobile || "—"}
                  </td>
                  <td className={`${tdClass} text-[#757575]`}>
                    {formatDate(row.createdAt)}
                  </td>
                  <td className={tdClass}>
                    <div className="flex items-center justify-center gap-2">
                      <button
                        className={deleteBtnClass}
                        onClick={() => confirmCancel(row)}
                      >
                        삭제하기
                      </button>
                      <button
                        className={approveBtnClass}
                        onClick={() => confirmApprove(row)}
                      >
                        승인하기
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 무한 스크롤 — sentinel 이 보이면 자동으로 다음 페이지 로드 */}
        {hasMore && (
          <div
            ref={sentinelRef}
            className="text-center text-[12px] text-[#888] py-4 mb-4"
          >
            {loading ? "불러오는 중..." : ""}
          </div>
        )}
        </div>
      </Pod24AdminLayout>
    </div>
  );
};